import { useFinanceFunction } from "../components/eex/use-finance-function";
import { canAccessFinancePath } from "@chro/shared";
import { useAuth } from "../stores/auth";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { api, selectedCompanyId } from "../lib/api";
import { cn } from "../lib/utils";
import { Td, Th, errMessage, withRecognitionConfirm, type GateExtras } from "../components/eex/ui";
import { useEexRole } from "../components/eex/use-eex-role";
import { MatchingPanel } from "../components/eex/MatchingPanel";
import { MonthlyFinanceReport } from "../components/eex/MonthlyFinanceReport";
import { parseFinanceOrigin } from "../features/eex/finance-navigation";
import { fmtEur0, fmtPx, fmtQty } from "../lib/eex-format";
import { useMobileUiV1State } from "../hooks/use-media-query";
import type {
  CellStateInfo,
  RecognitionCellSelection,
  RecognitionLine,
  RecognitionResponse,
} from "../features/eex/pnl/contracts";
import {
  buildRecognitionMonthsRequestPath,
  recognitionQueryKeys,
} from "../features/eex/pnl/queries";
import {
  recognitionCellLookupKey,
  recognitionContractKey,
  recognitionContractLabel as contractLabel,
  recognitionMonthLabel as monthLabel,
  recognitionRowLabel as rowKey,
  selectBookedVsSnapshotDrift,
  selectRecognitionLines,
  selectRecognitionTableFacts,
  selectSignedUnitMargin,
} from "../features/eex/pnl/selectors";

export const Route = createFileRoute("/eex/pnl")({
  validateSearch: parseFinanceOrigin,
  component: EexPnlPage,
});

// Authed CSV download (exports carry per-trader P&L — admin routes).
async function downloadCsv(path: string, filename: string): Promise<void> {
  const companyId = selectedCompanyId();
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    headers: companyId ? { "x-company-id": companyId } : {},
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error((body as { error?: string } | null)?.error ?? res.statusText);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function eurClass(v: number): string {
  return v > 0 ? "text-posGreen" : v < 0 ? "text-negRed" : "text-muted";
}

// ── page ───────────────────────────────────────────────────────────────────────

const BOOK_TRADER_TABLE_VISIBLE_KEY = "cascena.eex.pnl.book-trader-table-visible";

function EexPnlPage() {
  const financeOrigin = Route.useSearch();
  const { role } = useEexRole();
  const financeTabs = useAuth((state) => state.user?.eexFinanceTabs);
  const canViewReports = useFinanceFunction("pnl.reports");
  const canReport = role === "company_admin" || role === "finance" && canViewReports;
  const canExport = role === "company_admin" || role === "finance";
  const canClose = role === "company_admin"; // hard-close = company admin only (framework §8)
  const { isMobileViewport } = useMobileUiV1State();
  const q = useQuery({
    queryKey: recognitionQueryKeys.months(),
    queryFn: () => api<RecognitionResponse>(buildRecognitionMonthsRequestPath()),
  });
  const [sel, setSel] = useState<RecognitionCellSelection | null>(null);
  const [closeErr, setCloseErr] = useState<string | null>(null);
  const [showBookTraderTable, setShowBookTraderTable] = useState(() => {
    try {
      return window.localStorage.getItem(BOOK_TRADER_TABLE_VISIBLE_KEY) !== "false";
    } catch {
      return true;
    }
  });

  function toggleBookTraderTable() {
    const visible = !showBookTraderTable;
    setShowBookTraderTable(visible);
    if (!visible) setSel(null);
    try {
      window.localStorage.setItem(BOOK_TRADER_TABLE_VISIBLE_KEY, String(visible));
    } catch {
      // Keep the control usable when browser storage is unavailable.
    }
  }

  if (q.isLoading) return <div className="p-4 text-muted text-sm">Loading recognition…</div>;
  if (q.error) return <div className="p-4 text-negRed text-sm">{(q.error as Error).message}</div>;
  const data = q.data!;
  const { stateAt, months, rows, cellAt, bookedByMonth, staleContracts } =
    selectRecognitionTableFacts(data);
  const selLines = selectRecognitionLines(data, sel);

  return (
    <div className="space-y-4 px-3 py-4 sm:p-4">
      {data.recognitionScopeNotice && <p role="note" aria-label="Recognition scope" className="rounded border border-warnOrange/40 bg-warnOrange/10 p-3 text-sm">{data.recognitionScopeNotice}</p>}
      <div className="flex items-baseline gap-3 flex-wrap">
        <h1 className="text-lg font-medium">P&L by financial month</h1>
        {(role !== "finance" || canAccessFinancePath(financeTabs, "/eex/recognition-schedules")) && <Link target="_blank" rel="noopener noreferrer" to="/eex/recognition-schedules" className="text-xs text-accent hover:underline">Recognition schedules</Link>}
        <span className="text-xs text-muted">
          recognition basis: close-month / delivery-month @ FSP · FIFO · as of {data.asOf}
        </span>
        {canExport && (!isMobileViewport || role === "finance") && (
          <DesktopPnlHeaderActions
            canClose={canClose}
            closeError={closeErr}
            showError={!sel}
            onError={setCloseErr}
          />
        )}
      </div>

      {canReport && <MonthlyFinanceReport financeOrigin={financeOrigin} readOnly={role === "finance"} />}

      {canClose && data.pendingParcelPins.length > 0 && (
        <div className="rounded border border-warnOrange/40 bg-warnOrange/10 px-3 py-2 text-xs font-medium text-warnOrange sm:border-amber-500/40 sm:bg-amber-500/10 sm:font-normal sm:text-amber-600 sm:dark:text-amber-400">
          {data.pendingParcelPins.length} declared close(s) (pin-on-parcel) await your confirmation — open the
          position's "Fills & matching" to approve or reject.
        </div>
      )}
      {staleContracts.length > 0 && (
        <div className="rounded border border-warnOrange/40 bg-warnOrange/10 px-3 py-2 text-xs font-medium text-warnOrange sm:border-amber-500/40 sm:bg-amber-500/10 sm:font-normal sm:text-amber-600 sm:dark:text-amber-400">
          {staleContracts.length} contract(s) past delivery with a stale price store — their delivery
          P&L is BLOCKED from booking (import the missing statements):{" "}
          {staleContracts.map((k) => k.split("|")[0]).join(", ")}
        </div>
      )}
      {data.unmappedProducts.length > 0 && (
        <div className="text-xs text-muted">
          {data.unmappedProducts.length} unmapped product(s) not shown.
        </div>
      )}

      <button
        type="button"
        className="min-h-11 rounded border border-line px-3 py-2 text-xs hover:bg-line/40 sm:min-h-0"
        aria-expanded={showBookTraderTable}
        aria-controls="book-trader-pnl-table book-trader-pnl-legend"
        onClick={toggleBookTraderTable}
      >
        {showBookTraderTable ? "Hide" : "Show"} Book · Trader table
      </button>

      <div
        id="book-trader-pnl-table"
        hidden={!showBookTraderTable}
        className="overflow-x-auto rounded border border-line/60 sm:rounded-none sm:border-0"
        role="region"
        aria-label="P and L by financial month; scroll horizontally for additional months"
        tabIndex={0}
      >
        <table className="text-xs border-collapse w-full">
          <thead>
            <tr className="border-b border-line text-muted">
              <Th>Book · Trader</Th>
              {months.map((m) => (
                <Th key={m} className="text-right">{monthLabel(m)}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.k} className="border-b border-line/50">
                <Td className="font-medium">{r.k}</Td>
                {months.map((m) => {
                  const c = cellAt.get(`${r.k}|${m}`);
                  if (!c) return <Td key={m} className="text-right text-muted">·</Td>;
                  const isSel = sel?.book === c.book && sel?.trader === c.trader && sel?.month === m;
                  const st = stateAt.get(`${r.k}|${m}`);
                  // Booked-vs-live drift on a hard cell = the restatement signal.
                  const drift = selectBookedVsSnapshotDrift(c, st ?? null);
                  return (
                    <Td key={m} className="text-right">
                      <button
                        className={cn(
                          "min-h-11 w-full rounded px-2 py-1 text-right hover:bg-line/40 sm:min-h-0 sm:px-1 sm:py-0.5",
                          isSel && "bg-line/60",
                          st?.state === "soft" && "ring-1 ring-amber-500/40",
                          st?.state === "hard" && "ring-1 ring-posGreen/40"
                        )}
                        onClick={() => {
                          setSel(isSel ? null : { book: c.book, trader: c.trader, month: m });
                        }}
                        title={
                          st?.state === "hard"
                            ? `booked (hard-closed ${st.hardClosedAt?.slice(0, 10) ?? ""})`
                            : st?.state === "soft"
                              ? `provisional — under review until ${st.reviewWindowEnd ?? "?"}`
                              : `${c.finalCount} booked line(s), ${c.estimateCount} estimate(s)`
                        }
                      >
                        <span className={cn("tabular-nums font-medium", eurClass(c.bookedEUR))}>
                          {st?.state === "hard" && <span className="mr-0.5">🔒</span>}
                          {c.finalCount > 0 ? fmtEur0(c.bookedEUR) : "—"}
                        </span>
                        {drift !== 0 && (
                          <span className="block tabular-nums text-[10px] text-negRed">
                            Δ vs booked {fmtEur0(drift)}
                          </span>
                        )}
                        {c.estimateCount > 0 && (
                          <span className="block tabular-nums text-[10px] italic text-muted">
                            est {fmtEur0(c.estimateEUR)}
                            {c.staleCount > 0 && <span className="text-amber-500"> ⚠</span>}
                          </span>
                        )}
                        {c.fxMissingCount > 0 && (
                          <span className="block text-[10px] text-negRed">FX missing</span>
                        )}
                      </button>
                    </Td>
                  );
                })}
              </tr>
            ))}
            {/* Company GL roll-up: booked only — estimates NEVER sum into this row. */}
            <tr className="border-t border-line font-medium">
              <Td>Company (booked)</Td>
              {months.map((m) => {
                const v = bookedByMonth.get(m);
                return (
                  <Td key={m} className={cn("text-right tabular-nums", v != null ? eurClass(v) : "text-muted")}>
                    {v != null ? fmtEur0(v) : "·"}
                  </Td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <p id="book-trader-pnl-legend" hidden={!showBookTraderTable} className="text-[11px] text-muted max-w-3xl">
        Booked = recognized profit (parcels closed in that month at the close price, or delivered at
        the final settlement price). <em>est</em> = provisional, valued at the current settlement —
        display only, never booked, never summed into the company row. Deferred open MtM is not
        shown here (see Margin for the daily cash view).
      </p>

      {showBookTraderTable && sel && (
        <div className="space-y-2">
          <h2 className="text-sm font-medium">
            {rowKey(sel.book, sel.trader)} — {monthLabel(sel.month)}
            <button
              className="ml-3 min-h-11 px-2 text-xs text-muted hover:underline sm:min-h-0 sm:px-0"
              onClick={() => setSel(null)}
            >
              close
            </button>
          </h2>
          {(() => {
            const st = stateAt.get(recognitionCellLookupKey(sel.book, sel.trader, sel.month));
            return (
              <div className="flex items-center gap-3 text-xs flex-wrap">
                {st?.state === "hard" ? (
                  <span className="px-2 py-0.5 rounded bg-posGreen/15 text-posGreen">
                    BOOKED — hard-closed {st.hardClosedAt?.slice(0, 10)}; frozen at{" "}
                    {fmtEur0(st.snapshotTotalEur)} EUR
                  </span>
                ) : st?.state === "soft" ? (
                  <>
                    <span className="rounded bg-warnOrange/15 px-2 py-0.5 font-medium text-warnOrange sm:bg-amber-500/15 sm:font-normal sm:text-amber-600 sm:dark:text-amber-400">
                      PROVISIONAL — review window until {st.reviewWindowEnd ?? "?"}
                    </span>
                    {canClose && !isMobileViewport && (
                      <DesktopPnlHardCloseAction
                        selection={sel}
                        state={st}
                        expectedFingerprint={data.fingerprint}
                        onError={setCloseErr}
                      />
                    )}
                  </>
                ) : (
                  <span className="px-2 py-0.5 rounded bg-line/40 text-muted">
                    OPEN — not yet soft-closed
                  </span>
                )}
                {!isMobileViewport && closeErr && <span className="text-negRed">{closeErr}</span>}
              </div>
            );
          })()}
          {/* Reclass journal notes: money that LEFT this exact CELL (month ×
              book × trader) — the target side shows per-line "reclass ←"
              badges instead. */}
          {data.reclassApplications
            .flatMap((a) =>
              a.months
                .filter(
                  (m) =>
                    m.fromMonth === sel.month &&
                    (m.book ?? null) === (sel.book ?? null) &&
                    (m.trader ?? null) === (sel.trader ?? null)
                )
                .map((m) => ({ a, m }))
            )
            .map(({ a, m }) => (
              <div
                key={`${a.entryId}|${m.book ?? ""}|${m.trader ?? ""}`}
                className="rounded border border-violet-500/30 bg-violet-500/5 px-2 py-1 text-xs text-violet-700 dark:text-violet-300"
              >
                reclass &ldquo;{a.name}&rdquo;: {m.lots} lot(s), {fmtEur0(m.eur)} moved out of this cell
                {a.targetMonth ? ` → books ${monthLabel(a.targetMonth)}` : ""}
              </div>
            ))}
          {/* Fills & matching — up front, one toggle per contract in this cell.
              This is where you inspect the raw fills and DECLARE CLOSES. */}
          {!isMobileViewport && (
            <DesktopPnlMatchingActions
              key={recognitionCellLookupKey(sel.book, sel.trader, sel.month)}
              lines={selLines}
              book={sel.book}
              trader={sel.trader}
            />
          )}
          <div
            className="overflow-x-auto rounded border border-line/60 sm:rounded-none sm:border-0"
            role="region"
            aria-label="Recognition line detail; scroll horizontally for all fields"
            tabIndex={0}
          >
          <table className="min-w-max border-collapse text-xs sm:min-w-0">
            <thead>
              <tr className="border-b border-line text-muted">
                <Th>Pair</Th>
                <Th>Product</Th>
                <Th>Period</Th>
                <Th>Kind</Th>
                <Th>Side</Th>
                <Th className="text-right">Entry</Th>
                <Th>Entry leg</Th>
                <Th className="text-right">Exit</Th>
                <Th>Exit leg</Th>
                <Th className="text-right">Δ px</Th>
                <Th className="text-right">Lots</Th>
                <Th className="text-right">Volume</Th>
                <Th className="text-right">Native</Th>
                <Th className="text-right">EUR</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {selLines.map((l, i) => (
                <Fragment key={i}>
                  <tr className="border-b border-line/40">
                    <Td>
                      {l.pair ? (
                        <span className="font-medium">{l.pair}</span>
                      ) : (
                        <span className="text-muted italic">—</span>
                      )}
                    </Td>
                    <Td>{l.productLabel ?? l.contract.code}</Td>
                    <Td className="font-mono">{l.periodLabel ?? contractLabel(l.contract)}</Td>
                    <Td>
                      {l.exitKind === "close"
                        ? `closed ${l.exitDate ?? l.schedule?.sourceDate ?? l.recognitionDate}`
                        : l.finality === "final"
                          ? "delivered @ FSP"
                          : `est @ DSP ${
                              data.exitVerdicts[recognitionContractKey(l.contract)]?.priceDate ?? "?"
                            }`}
                      {l.reclass && (
                        <span
                          className="ml-1.5 px-1 py-0.5 rounded text-[10px] bg-violet-500/15 text-violet-600 dark:text-violet-400"
                          title={`reclassified here by "${l.reclass.name}" — the engine books this in ${monthLabel(l.reclass.fromMonth)}`}
                        >
                          reclass ← {monthLabel(l.reclass.fromMonth)}
                        </span>
                      )}
                      {l.schedule && <Link target="_blank" rel="noopener noreferrer" to="/eex/recognition-schedules" search={l.pair?.trim() ? { pair: l.pair } : { scope: "unpaired" }} className="ml-1.5 text-xs text-accent underline" title={l.schedule.reason}>
                        {l.schedule.remainder ? "automatic remainder" : `scheduled ${l.schedule.percent}% ← ${monthLabel(l.schedule.sourceMonth)}`}
                      </Link>}
                    </Td>
                    <Td>
                      {l.side === "B" ? (
                        <span className="text-posGreen">long</span>
                      ) : l.side === "S" ? (
                        <span className="text-negRed">short</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </Td>
                    <Td className="text-right tabular-nums">{fmtPx(l.entry, 2)}</Td>
                    <Td className="text-[11px]">
                      <span className={cn(l.entrySource.includes("cascade") && "text-sky-500")}>
                        {l.entrySource}
                      </span>
                      {l.entryDate && <span className="text-muted"> · {l.entryDate}</span>}
                    </Td>
                    <Td className="text-right tabular-nums">{fmtPx(l.exit, 2)}</Td>
                    <Td className="text-[11px]">
                      <span className={cn(l.exitSource.includes("cascade") && "text-sky-500")}>
                        {l.exitSource}
                      </span>
                      {l.exitDate && <span className="text-muted"> · {l.exitDate}</span>}
                    </Td>
                    {(() => {
                      // Signed per-unit margin in the SAME unit as Entry/Exit
                      // (priceUnit — pence stay pence): a short profits when
                      // entry > exit. Matches Positions' realized €/MWh.
                      const dpx = selectSignedUnitMargin(l);
                      return (
                        <Td className={cn("text-right tabular-nums", eurClass(dpx))}>
                          <span title={`${l.priceUnit} — signed per-unit margin`}>
                            {fmtPx(dpx, 2)}
                          </span>
                        </Td>
                      );
                    })()}
                    <Td className="text-right tabular-nums">{fmtQty(l.lots)}</Td>
                    <Td className="text-right tabular-nums">
                      {fmtQty(l.volume)} {l.unit}
                    </Td>
                    <Td className="text-right tabular-nums">
                      {fmtEur0(l.nativeAmount)} {l.nativeCcy !== "EUR" ? l.nativeCcy : ""}
                      {l.fxRate != null && l.nativeCcy !== "EUR" && (
                        <span className="text-muted"> @{fmtPx(l.fxRate, 4)}</span>
                      )}
                    </Td>
                    <Td className={cn("text-right tabular-nums font-medium", l.recognizedEUR != null ? eurClass(l.recognizedEUR) : "text-negRed")}>
                      {l.recognizedEUR != null ? fmtEur0(l.recognizedEUR) : "no FX"}
                    </Td>
                    <Td>
                      <span
                        className={cn(
                          "px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide",
                          l.finality === "final"
                            ? "bg-posGreen/15 text-posGreen"
                            : l.finality === "stale"
                              ? "bg-negRed/15 text-negRed"
                              : "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                        )}
                      >
                        {l.finality === "final" ? "booked" : l.finality}
                      </span>
                    </Td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

function DesktopPnlHeaderActions({
  canClose,
  closeError,
  showError,
  onError,
}: {
  canClose: boolean;
  closeError: string | null;
  showError: boolean;
  onError: (error: string | null) => void;
}) {
  const qc = useQueryClient();
  const canExportBonus = useFinanceFunction("pnl.export_bonus");
  const canExportGl = useFinanceFunction("pnl.export_gl");
  const softRun = useMutation({
    mutationFn: () =>
      api<{ closed: unknown[] }>("/eex/recognition/close/soft-run", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: recognitionQueryKeys.months() }),
  });

  return (
    <div className="contents">
      {canClose && <button
        className="rounded border border-line px-2 py-0.5 text-xs hover:bg-line/40 disabled:opacity-50"
        disabled={softRun.isPending}
        onClick={() => softRun.mutate()}
        title="Soft-close every past, FSP-complete month cell now (the daily job does this automatically)"
      >
        {softRun.isPending ? "sweeping…" : "Run soft-close sweep"}
      </button>}
      {canExportBonus && <button
        className="rounded border border-line px-2 py-0.5 text-xs hover:bg-line/40"
        title="Bonusing extract: one row per BOOKED (hard-closed) book·trader·month — draft format for comp to confirm"
        onClick={() =>
          downloadCsv("/eex/recognition/export/bonus.csv", "eex-bonus.csv").catch((error) =>
            onError(errMessage(error))
          )
        }
      >
        ⬇ Bonus CSV
      </button>}
      {canExportGl && <button
        className="rounded border border-line px-2 py-0.5 text-xs hover:bg-line/40"
        title="GL hand-off: line-grain detail behind every booked figure, from the frozen snapshots — draft format for accounting to confirm"
        onClick={() =>
          downloadCsv("/eex/recognition/export/gl.csv", "eex-gl.csv").catch((error) =>
            onError(errMessage(error))
          )
        }
      >
        ⬇ GL CSV
      </button>}
      {closeError && showError && <span className="text-xs text-negRed">{closeError}</span>}
    </div>
  );
}

function DesktopPnlHardCloseAction({
  selection,
  state,
  expectedFingerprint,
  onError,
}: {
  selection: RecognitionCellSelection;
  state: CellStateInfo;
  expectedFingerprint: string;
  onError: (error: string | null) => void;
}) {
  const qc = useQueryClient();
  const hardClose = useMutation({
    mutationFn: () =>
      api("/eex/recognition/close/hard", {
        method: "POST",
        body: JSON.stringify({ ...selection, expectedFingerprint }),
      }),
    onSuccess: () => {
      onError(null);
      void qc.invalidateQueries({ queryKey: recognitionQueryKeys.months() });
    },
    onError: (error) => onError(errMessage(error)),
  });

  return (
    <button
      className="rounded border border-posGreen/50 px-2 py-0.5 text-posGreen hover:bg-posGreen/10 disabled:opacity-50"
      disabled={hardClose.isPending || !state.ready}
      title={
        state.ready
          ? "Freeze this cell's booked lines and send to accounting (irreversible without restatement)"
          : `blocked: ${state.blockers.join(", ")}`
      }
      onClick={() => {
        if (
          window.confirm(
            `Hard-close ${rowKey(selection.book, selection.trader)} ${selection.month}? This books the figure — changes after this require a restatement.`
          )
        ) {
          hardClose.mutate();
        }
      }}
    >
      {hardClose.isPending ? "booking…" : "Hard close (book it)"}
    </button>
  );
}

function DesktopPnlMatchingActions({
  lines,
  book,
  trader,
}: {
  lines: RecognitionLine[];
  book: string | null;
  trader: string | null;
}) {
  const [openContract, setOpenContract] = useState<string | null>(null);
  const contracts = [
    ...new Map(
      lines.map((line) => [
        `${line.contract.code}|${line.contract.expYear}|${line.contract.expMonth}`,
        line.contract,
      ])
    ).entries(),
  ];

  return (
    <div className="space-y-2 rounded border border-line/60 bg-line/10 px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted">Inspect fills &amp; matching / declare a close:</span>
        {contracts.map(([key, contract]) => (
          <button
            key={key}
            className={cn(
              "rounded border px-2 py-0.5 text-xs",
              openContract === key
                ? "border-accent bg-accent/10 text-accent"
                : "border-line hover:bg-line/40"
            )}
            onClick={() => setOpenContract(openContract === key ? null : key)}
          >
            {contractLabel(contract)} {openContract === key ? "▾" : "▸"}
          </button>
        ))}
      </div>
      {openContract && (() => {
        const contract = contracts.find(([key]) => key === openContract)?.[1];
        return contract ? (
          <MatchingPanel key={openContract} target={contract} book={book} trader={trader} />
        ) : null;
      })()}
    </div>
  );
}
